// 화물운송 길잡이 (/guide) — 관리자·공개 화면 공용
import { avatarAssetUrl, type VideoStatus } from '@/lib/avatar-chat';

export const GUIDES = [
  {
    key: 'start',
    label: '화물운송 시작하기',
    short: '시작할 때',
    desc: '자격증, 번호판, 차량, 보험, 자금, 신고, 운송 업무까지 처음부터 차례대로 안내합니다.',
  },
  {
    key: 'exit',
    label: '화물운송 정리하기',
    short: '그만둘 때',
    desc: '번호판 양도, 차량 처분, 폐업 신고, 계약 정리까지 손해 없이 마무리하는 순서를 안내합니다.',
  },
] as const;

export type GuideKey = (typeof GUIDES)[number]['key'];
export const isGuideKey = (v: unknown): v is GuideKey => GUIDES.some((g) => g.key === v);
export const guideDef = (k: GuideKey) => GUIDES.find((g) => g.key === k)!;

// 법령 확인일이 이 개월 수를 넘기면 관리자 화면에 "재확인 필요"
export const GUIDE_RECHECK_MONTHS = 6;

export type GuideSource = { title: string; url: string; date: string | null };

export type GuideItem = {
  id: string;
  guide: GuideKey;
  chapter: string;
  question: string;
  answer: string;
  video_script: string | null;
  checklist: string[];
  sources: GuideSource[];
  verified_at: string | null;
  admin_note: string | null;
  is_active: boolean;
  sort_order: number;
  video_status: VideoStatus;
  heygen_video_id: string | null;
  video_path: string | null;
  thumbnail_path: string | null;
  duration_sec: number | null;
  video_script_used: string | null;
  video_error: string | null;
  created_at: string;
  updated_at: string;
};

export const GUIDE_SELECT =
  'id, guide, chapter, question, answer, video_script, checklist, sources, verified_at, admin_note, is_active, sort_order, video_status, heygen_video_id, video_path, thumbnail_path, duration_sec, video_script_used, video_error, created_at, updated_at';

// 영상으로 읽을 대본 (영상용 대본이 없으면 답변 전체)
export const scriptOf = (it: Pick<GuideItem, 'video_script' | 'answer'>) => (it.video_script?.trim() || it.answer).trim();

export function needsRecheck(verifiedAt: string | null, now = new Date()) {
  if (!verifiedAt) return true;
  const d = new Date(verifiedAt);
  d.setMonth(d.getMonth() + GUIDE_RECHECK_MONTHS);
  return d < now;
}

// 공개 화면에 보내는 값
export type PublicGuideItem = {
  id: string;
  chapter: string;
  question: string;
  answer: string;
  checklist: string[];
  sources: GuideSource[];
  verified_at: string | null;
  video: { title: string; video_url: string; thumbnail_url: string | null } | null;
};

export function toPublicGuideItem(it: GuideItem): PublicGuideItem {
  const hasVideo = it.video_status === 'completed' && !!it.video_path;
  return {
    id: it.id,
    chapter: it.chapter,
    question: it.question,
    answer: it.answer,
    checklist: it.checklist ?? [],
    sources: it.sources ?? [],
    verified_at: it.verified_at,
    video: hasVideo
      ? {
          title: it.question,
          video_url: avatarAssetUrl(it.video_path!),
          thumbnail_url: it.thumbnail_path ? avatarAssetUrl(it.thumbnail_path) : null,
        }
      : null,
  };
}

// 단계별 주소: /guide/start/236b8930 (항목 id 앞 8자리. 질문을 고치거나 순서를 바꿔도 주소는 그대로)
export const guideItemSlug = (id: string) => id.slice(0, 8);
export const guideItemPath = (guide: GuideKey, id: string) => `/guide/${guide}/${guideItemSlug(id)}`;

// 검색 결과 설명: 답변의 "한 줄 답:" 줄 (없으면 답변 앞부분)
export function guideSummary(answer: string, max = 150) {
  const line = answer.split('\n').map((l) => l.trim()).find((l) => /^한 줄 답\s*[:：]/.test(l));
  const text = (line ? line.replace(/^한 줄 답\s*[:：]\s*/, '') : answer).replace(/\*\*/g, '').replace(/^(📘|⚠️)\s*/gm, '').replace(/\s+/g, ' ').trim();
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

// 장마다 붙이는 설명 그림 (guide_chapters)
export type GuideChapter = { guide: GuideKey; chapter: string; image_path: string | null; image_alt: string | null; updated_at: string };
export type ChapterImage = { url: string; alt: string };
export type ChapterImages = Record<string, ChapterImage>;

export function toChapterImages(rows: GuideChapter[]): ChapterImages {
  const out: ChapterImages = {};
  for (const r of rows) if (r.image_path) out[r.chapter] = { url: avatarAssetUrl(r.image_path), alt: r.image_alt || `${r.chapter} 설명 그림` };
  return out;
}

// 장 순서 = 그 장의 첫 항목 순서. 같은 장 안에서는 sort_order 순
export function groupByChapter<T extends { chapter: string }>(items: T[]) {
  const out: { chapter: string; items: T[] }[] = [];
  for (const it of items) {
    const g = out.find((c) => c.chapter === it.chapter);
    if (g) g.items.push(it);
    else out.push({ chapter: it.chapter, items: [it] });
  }
  return out;
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

// 관리자 등록/수정 값 검증
export function parseGuideBody(b: unknown) {
  const v = (b ?? {}) as Record<string, unknown>;
  const guide = v.guide;
  const chapter = String(v.chapter ?? '').trim();
  const question = String(v.question ?? '').trim();
  const answer = String(v.answer ?? '').trim();
  const script = String(v.video_script ?? '').trim();
  const checklist = (Array.isArray(v.checklist) ? (v.checklist as unknown[]) : String(v.checklist ?? '').split('\n'))
    .map((s) => String(s).trim())
    .filter(Boolean)
    .slice(0, 12)
    .map((s) => s.slice(0, 200));
  const sources = (Array.isArray(v.sources) ? (v.sources as unknown[]) : [])
    .map((s) => {
      const o = (s ?? {}) as Record<string, unknown>;
      const date = String(o.date ?? '').trim();
      return { title: String(o.title ?? '').trim().slice(0, 200), url: String(o.url ?? '').trim().slice(0, 1000), date: DATE_RE.test(date) ? date : null };
    })
    .filter((s) => s.title || s.url)
    .slice(0, 8);
  const verified = String(v.verified_at ?? '').trim();
  const note = String(v.admin_note ?? '').trim().slice(0, 2000);

  if (!isGuideKey(guide)) return { error: '매뉴얼 종류가 올바르지 않습니다.' } as const;
  if (!chapter || chapter.length > 40) return { error: '장 이름은 1~40자로 입력해주세요.' } as const;
  if (!question || question.length > 300) return { error: '질문은 1~300자로 입력해주세요.' } as const;
  if (!answer || answer.length > 4000) return { error: '답변은 1~4000자로 입력해주세요.' } as const;
  if (script.length > 1500) return { error: '영상 대본은 1500자 이내로 입력해주세요.' } as const;
  if (sources.some((s) => s.url && !/^https?:\/\//.test(s.url))) return { error: '출처 주소는 http(s):// 로 시작해야 합니다.' } as const;
  if (verified && !DATE_RE.test(verified)) return { error: '법령 확인일 형식이 올바르지 않습니다.' } as const;

  return {
    guide,
    chapter,
    question,
    answer,
    video_script: script || null,
    checklist,
    sources,
    verified_at: verified || null,
    admin_note: note || null,
    is_active: v.is_active !== false,
  } as const;
}
